/**
 * ============================================================================
 * ImageModal - Full-Screen Image Viewer
 * ============================================================================
 *
 * Lightbox-style modal for viewing review images
 * - Keyboard navigation (←/→ arrows, Esc to close)
 * - Touch/swipe support for mobile
 * - Image counter
 * - Smooth transitions
 *
 * ============================================================================
 */

import React, { useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import Modal from '../ui/Modal';

interface ImageModalProps {
  isOpen?: boolean;
  images: string[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

export function ImageModal({ isOpen = true, images, currentIndex, onClose, onNavigate }: ImageModalProps) {
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < images.length - 1;

  // Keyboard navigation for arrows (ESC handled by Modal)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && canGoPrev) onNavigate(currentIndex - 1);
      if (e.key === 'ArrowRight' && canGoNext) onNavigate(currentIndex + 1);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, canGoPrev, canGoNext, onNavigate]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} variant="lightbox" animate={false}>
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-white bg-opacity-10 hover:bg-opacity-20 rounded-full text-white transition-all z-10"
        aria-label="Close"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Image Counter */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-white bg-opacity-10 rounded-full text-white text-sm font-medium z-10">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Previous Button */}
      {canGoPrev && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(currentIndex - 1);
          }}
          className="absolute left-4 p-3 bg-white bg-opacity-10 hover:bg-opacity-20 rounded-full text-white transition-all z-10"
          aria-label="Previous image"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}

      {/* Image */}
      <div className="max-w-6xl max-h-[90vh] px-16">
        <img
          src={images[currentIndex]}
          alt={`${currentIndex + 1} of ${images.length}`}
          className="max-w-full max-h-[90vh] object-contain rounded-lg"
        />
      </div>

      {/* Next Button */}
      {canGoNext && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(currentIndex + 1);
          }}
          className="absolute right-4 p-3 bg-white bg-opacity-10 hover:bg-opacity-20 rounded-full text-white transition-all z-10"
          aria-label="Next image"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      )}
    </Modal>
  );
}
